import * as Sentry from "@sentry/nextjs"
import { Button, Label, Spinner } from "flowbite-react"
import React, { useCallback, useEffect, useState } from "react"
import { useAsyncDebounce } from "react-table"
import useSWR from "swr"

import DangerouslyHTMLTextPriview from "@components/elements/DangerouslyHTMLTextPriview"
import MonoImageGallery from "@components/elements/MonoImageGallery"
import MonoSelect from "@components/elements/Select/MonoSelect"
import RightArrow from "@icons/RightArrow"
import * as ProductPricingServices from "@mono/essentials/services/ProductPricingServices"

import { createDropdownOptionsForUnit } from "../../../../utils/common"
import { ProductPriceTypeData } from "../Preview/ProductDetailsPreview"

const MerchandizeViewDetailsModal = ({
  handleNextStep,
  selectedProductDetail,
  isIntegratedPage,
  setProductInfo,
  isLoading,
  isSubmitting,
  domainDetails,
  setOrderSummery,
}) => {
  const [selectedPriceTypeData, setSelectedPriceTypeData] = useState<any[]>([])
  const images = selectedProductDetail?.mediaFiles?.map((image: any) => {
    return {
      original: `${image.public_url}`,
      thumbnail: `${image.public_url}`,
    }
  })

  const {
    data: productPriceData,
    mutate: refetchProductPriceData,
    isLoading: isProductPriceLoading,
  } = useSWR(
    selectedProductDetail?.id ? `productPriceData-${selectedProductDetail?.id}` : null,
    async () => {
      const { data: getProductPriceDataResponse } =
        await ProductPricingServices.getProductPricingByProdId(
          selectedProductDetail?.id ?? "",
          {},
          isIntegratedPage,
          ""
        )
      if (getProductPriceDataResponse) {
        return getProductPriceDataResponse.data
      }
    },
    {
      revalidateOnFocus: false,
    }
  )

  const fetchCapacityData = useAsyncDebounce(async (allPriceData) => {
    try {
      const _allData = allPriceData?.map((price) => {
        return {
          unit_id: price.unitId,
          quantity: price.unitCount,
        }
      })

      const payload = {
        quantities: _allData,
      }

      const { data } = await ProductPricingServices.getMerchandisePricingByQuantities(
        selectedProductDetail?.id,
        payload,
        isIntegratedPage
      )
      const capacityData = [...data]

      const mergedArray = capacityData.map((item1) => {
        const matchingItem = allPriceData.find((item2) => item2.unitId === item1.unit_id)
        return {
          ...matchingItem,
          ...item1,
        }
      })
      setOrderSummery({
        selectedPriceTypeData: mergedArray,
      })
      setSelectedPriceTypeData(mergedArray)
    } catch (e) {
      Sentry.captureException(e)
      console.error(e)
    }
  }, 500)

  useEffect(() => {
    if (productPriceData?.length) {
      const _price_data = productPriceData?.map((productPrice) => ({
        unitName: productPrice.unit?.name,
        unitId: productPrice.unit?.id,
        unitCount: 0,
        unitDescription: productPrice.unit?.description || "",
        price: productPrice.formatted_price,
        unitPrice: productPrice?.price,
      }))
      fetchCapacityData([..._price_data])
    }
  }, [productPriceData])

  const handleProductPrice = (value: number, id: number, isSelected: boolean) => {
    try {
      const _oldData = selectedPriceTypeData
      for (const index in _oldData) {
        if (Number(index) === id) {
          _oldData[index].unitCount = isSelected ? value : _oldData[index].unitCount + value
          break
        }
      }
      setSelectedPriceTypeData([..._oldData])
      fetchCapacityData([..._oldData])
    } catch (e) {
      Sentry.captureException(e)
      console.log({ e })
    }
  }

  const totalUnitCount = selectedPriceTypeData?.reduce((total, item) => total + item?.unitCount, 0)

  const handleCounter = useCallback(() => {
    return (
      <>
        {(selectedPriceTypeData || [])?.map((product: any, index: number) => {
          const arraylength =
            product.unitCount > 0
              ? product.unitCount +
                (selectedProductDetail?.merchandise?.maximum_purchasable_quantity +
                  1 -
                  totalUnitCount)
              : selectedProductDetail?.merchandise?.maximum_purchasable_quantity +
                1 -
                totalUnitCount

          const countOptions = createDropdownOptionsForUnit(arraylength)
          return (
            <div
              className={"flex justify-between items-center my-2"}
              data-cy={"variant-card"}
              key={product?.uuid}
            >
              <div>
                <div className={"flex items-center"}>
                  <span
                    className={"text-neutral-1000 text14Medium text-wrap"}
                  >{`${product?.unitName}`}</span>
                  {product?.unitDescription && (
                    <span
                      className={"text14Medium text-neutral-500 text-wrap line-clamp-1"}
                    >{`(${product?.unitDescription})`}</span>
                  )}
                </div>
                <div className={"flex items-center gap-3  mt-2"}>
                  <div>
                    {domainDetails?.quantity_indicator === "increment_decrement_button" ? (
                      <div>
                        <div className={"ml-1 flex items-center gap-3"}>
                          <div>
                            <button
                              type={"button"}
                              tabIndex={-1}
                              disabled={product.unitCount === 0}
                              onClick={() => handleProductPrice(-1, index, false)}
                            >
                              <span
                                className={`m-auto block h-8 w-8 rounded-md text-xl  ${
                                  product.unitCount === 0
                                    ? "text-primary-400 cursor-not-allowed bg-primary-200"
                                    : "text-primary-600 bg-primary-300"
                                } `}
                              >
                                −
                              </span>
                            </button>
                          </div>
                          <div
                            className={
                              "flex h-8 w-8 rounded-md items-center justify-center border border-neutral-450 px-1 py-1.5"
                            }
                          >
                            <div className={"text15Medium"}>{product.unitCount}</div>
                          </div>
                          <div>
                            <button
                              type={"button"}
                              tabIndex={-1}
                              disabled={
                                totalUnitCount ===
                                selectedProductDetail?.merchandise?.maximum_purchasable_quantity
                              }
                              onClick={() => handleProductPrice(1, index, false)}
                            >
                              <span
                                className={`m-auto block h-8 w-8 rounded-md text-xl  ${
                                  totalUnitCount ===
                                  selectedProductDetail?.merchandise?.maximum_purchasable_quantity
                                    ? "text-primary-400 cursor-not-allowed bg-primary-200"
                                    : "text-primary-600 bg-primary-300"
                                }`}
                              >
                                +
                              </span>
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <MonoSelect
                        value={{
                          label: product?.unitCount,
                          value: product?.unitCount,
                        }}
                        options={countOptions}
                        onValueChange={(newValue) => {
                          handleProductPrice(newValue?.value, index, true)
                        }}
                        menuPosition={"fixed"}
                        className={"!w-44"}
                        name={"select_unit_count"}
                        isSearchable={false}
                      />
                    )}
                  </div>
                  <div
                    className={
                      "bg-primary-50 mx-2 my-1 tracking-wide rounded-full border-primary-600 border px-2 text-center"
                    }
                  >
                    <span className={"text-primary-600 text14Semibold"}>
                      {product.formatted_unit_price}
                    </span>
                    {product?.unitCount > 0 && (
                      <span
                        className={"text14Regular text-primary-600"}
                      >{`x${product?.unitCount}`}</span>
                    )}
                  </div>
                </div>
              </div>
              {product?.unitCount > 0 ? (
                <div className={"text14Semibold"}>{`${product?.formatted_price}`}</div>
              ) : null}
            </div>
          )
        })}
      </>
    )
  }, [selectedPriceTypeData])

  const gotoNextStep = () => {
    const unitsDataofSelectedProduct = selectedPriceTypeData
      ?.filter((item) => item.unitCount > 0)
      ?.map((item) => {
        return {
          unitName: item.unitName,
          unitId: item.unitId,
          unitCount: item.unitCount,
          price: item.price,
        }
      })

    const selectedProductInfo = {
      units: unitsDataofSelectedProduct,
    }
    setProductInfo?.(selectedProductInfo)
    handleNextStep?.(selectedProductInfo)
  }

  const hasNoParticipants = selectedPriceTypeData.filter((item) => item.unitCount > 0).length === 0

  if (isProductPriceLoading || isLoading)
    return (
      <div className={"py-5 min-h-[200px] flex items-center justify-center"}>
        <Spinner size={"md"} light />
      </div>
    )

  return (
    <>
      <div className={"mb-3"}>
        {selectedProductDetail &&
        selectedProductDetail?.mediaFiles &&
        selectedProductDetail?.mediaFiles?.length > 0 ? (
          <MonoImageGallery images={images || []} ContainerClassName={"w-full"} />
        ) : (
          <div
            className={
              "relative flex h-full min-h-[300px] max-[425px]:min-h-[220px] w-full cursor-pointer flex-row items-end rounded-lg bg-imagePlaceholderBackground bg-cover bg-center shadow-sm "
            }
          >
            <div
              className={
                "absolute left-1/2 top-1/2 h-11 w-[52px] -translate-x-1/2 -translate-y-1/2 transform bg-transparent bg-imagePlaceholder bg-contain bg-center bg-no-repeat"
              }
            />
          </div>
        )}
      </div>
      <div className={"text16Semibold mb-2"}>Details</div>
      <DangerouslyHTMLTextPriview
        content={selectedProductDetail?.description?.toString() ?? ""}
        className={"paragraph14Regular text-neutral-600 m-2"}
      />
      {handleCounter()}
      <div className={"pt-4 float-right"}>
        <Button
          color={"primary"}
          className={"w-full [&>span]:!text16Medium [&>span]:!py-[13px] !h-10"}
          onClick={gotoNextStep}
          disabled={hasNoParticipants || isSubmitting}
        >
          {isSubmitting ? (
            <Spinner size={"sm"} light />
          ) : (
            <>
              Next
              <RightArrow className={"ml-2.5 text-whiteBlack-100"} height={20} width={20} />
            </>
          )}
        </Button>
      </div>
    </>
  )
}

export default MerchandizeViewDetailsModal
